import React, { useState, useEffect, useMemo } from 'react';
import { ETIMFeature } from '../types/models';

interface EditableFeatureBadgeProps {
  availableFeatures: ETIMFeature[];
  initialFeature?: ETIMFeature;
  onFeatureChange: (featureCode: string | null, value: string | null) => void;
  onRemove?: () => void;
  isPending?: boolean;
}

const EditableFeatureBadge: React.FC<EditableFeatureBadgeProps> = ({
  availableFeatures,
  initialFeature,
  onFeatureChange,
  onRemove,
  isPending,
}) => {
  const [selectedFeatureCode, setSelectedFeatureCode] = useState<string | null>(initialFeature?.FEATURE_CODE || null);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    initialFeature?.VALUE !== undefined ? (initialFeature.VALUE === "" ? "" : initialFeature.VALUE) : null
  );

  // Синхронизируем локальное состояние с initialFeature при его изменении
  // Синхронизируем локальное состояние с initialFeature и обновляем опции при изменении availableFeatures
  useEffect(() => {
    const initialCode = initialFeature?.FEATURE_CODE || null;
    const initialVal = initialFeature?.VALUE !== undefined ? (initialFeature.VALUE === "" ? "" : initialFeature.VALUE) : null;

    // Всегда обновляем локальное состояние при изменении initialFeature
    setSelectedFeatureCode(initialCode);
    setSelectedValue(initialVal);

    // При изменении availableFeatures или initialFeature, useMemo для selectedFeature и displayFeatures
    // автоматически пересчитаются, обновляя опции в dropdown.
    // Нет необходимости явно обновлять их здесь.

  }, [initialFeature, availableFeatures]); // Зависит от initialFeature и availableFeatures

  // Определяем выбранную характеристику
  const selectedFeature = useMemo(() => {
    // Сначала ищем в availableFeatures
    let found = availableFeatures.find(f => f.FEATURE_CODE === selectedFeatureCode);
    // Если не нашли и код совпадает с initialFeature, используем initialFeature
    if (!found && initialFeature?.FEATURE_CODE === selectedFeatureCode) {
      found = initialFeature;
    }
    return found;
  }, [availableFeatures, selectedFeatureCode, initialFeature]);

  // Формируем список характеристик для отображения в первом dropdown
  const displayFeatures = useMemo(() => {
    const featuresMap = new Map<string, ETIMFeature>();
    
    // Добавляем все доступные характеристики
    availableFeatures.forEach(f => featuresMap.set(f.FEATURE_CODE, f));
    
    // Если есть initialFeature и его нет в карте, добавляем его
    if (initialFeature && !featuresMap.has(initialFeature.FEATURE_CODE)) {
      featuresMap.set(initialFeature.FEATURE_CODE, initialFeature);
    }
    
    // Преобразуем карту обратно в массив и сортируем (опционально)
    return Array.from(featuresMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    
  }, [availableFeatures, initialFeature]);

  // Формируем список доступных значений для второго dropdown
  const availableValues = useMemo<string[]>(() => {
    // Используем значения из selectedFeature (которое может быть initialFeature)
    const values = new Set<string>(selectedFeature?.values || []);
    
    // Добавляем initialFeature.VALUE, если оно есть и отсутствует
    if (initialFeature?.VALUE !== undefined && initialFeature.VALUE !== null) {
      values.add(initialFeature.VALUE);
    }
    
    // Преобразуем Set обратно в массив и сортируем (опционально)
    return Array.from(values).sort();
    
  }, [selectedFeature, initialFeature?.VALUE]);


  const handleFeatureChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newFeatureCode = event.target.value === "" ? null : event.target.value;
    setSelectedFeatureCode(newFeatureCode);
    setSelectedValue(null); // Сбрасываем значение при смене характеристики
    onFeatureChange(newFeatureCode, null);
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value === "" ? null : event.target.value;
    setSelectedValue(newValue);
    onFeatureChange(selectedFeatureCode, newValue);
  };

  return (
    <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium mr-2 mb-2 ${isPending ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
      {/* Обертка для позиционирования стрелки */}
      <div className="relative inline-flex items-center">
        {/* Dropdown for Feature */}
        <select
          className="bg-transparent text-blue-700 focus:outline-none cursor-pointer truncate appearance-none w-auto pr-4" // Добавили pr-4 (padding-right)
          value={selectedFeatureCode || ""}
          onChange={handleFeatureChange}
          title={selectedFeature?.name}
          // Добавляем стили для фона со стрелкой SVG
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.1rem center', // Позиция стрелки
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1.2em 1.2em', // Размер стрелки
          }}
        >
          <option value="">Выберите характеристику</option>
          {displayFeatures.map(feature => (
            <option key={feature.FEATURE_CODE} value={feature.FEATURE_CODE}>
              {feature.name}
            </option>
          ))}
        </select>
        {/* Можно убрать иконку, если используется фоновое изображение */}
        {/* <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-gray-700">
          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
        </div> */}
      </div>

      <span className="text-gray-500 mx-1">|</span>

      {/* Обертка для позиционирования стрелки */}
      <div className="relative inline-flex items-center">
        {/* Dropdown for Value */}
        <select
          className="bg-transparent text-blue-700 focus:outline-none cursor-pointer disabled:opacity-50 truncate appearance-none w-auto pr-4" // Добавили pr-4
          value={selectedValue ?? ""}
          onChange={handleValueChange}
          disabled={!selectedFeatureCode}
          title={selectedValue ?? ""}
           // Добавляем стили для фона со стрелкой SVG
           style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.1rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1.2em 1.2em',
          }}
        >
          <option value="">Выберите значение</option>
          {availableValues.map((value, index) => (
            <option key={index} value={value}>
              {value}
            </option>
          ))}
          {selectedFeatureCode && availableValues.length === 0 && (
              <option value="" disabled>Нет значений</option>
          )}
        </select>
         {/* Можно убрать иконку, если используется фоновое изображение */}
        {/* <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-gray-700">
          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
        </div> */}
      </div>

      {/* Кнопка удаления */}
      {onRemove && (
        <button
          className="ml-1 text-blue-700 hover:text-blue-900 focus:outline-none"
          onClick={onRemove}
          aria-label="Удалить характеристику"
        >
          &times;
        </button>
      )}
    </div>
  );
};

export default EditableFeatureBadge;